import React, { useState, useEffect, useCallback } from 'react';
import { Steps, Card, Button, Tag, Space, Modal, Input, Badge, Typography, Tooltip, Descriptions, Select, message, Progress, Alert } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
  PauseCircleOutlined,
  CloseCircleOutlined,
  RightOutlined,
  FileTextOutlined,
  SwapOutlined,
  EyeOutlined,
  RetweetOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { WorkflowStatusResponse, PhaseInfo, RfoPhase } from '@/types/workflow';
import type { ReviewGateDto } from '@/types/review';
import type { User } from '@/types/common';
import { getPhaseColor, getPhaseDeliverables, getPhaseDescription } from '@/utils/phaseHelpers';
import { workflowApi } from '@/api/workflowApi';
import { userApi } from '@/api/userApi';
import { analysisApi } from '@/api/analysisApi';
import { opportunityApi, type AssignedCandidate } from '@/api/opportunityApi';
import ReviewSubGates from './ReviewSubGates';
import GateDocuments from './GateDocuments';
import BidDecisionForm from './BidDecisionForm';
import IntakeDetailsForm from './IntakeDetailsForm';
import KickoffPlanForm from './KickoffPlanForm';
import FeasibilityReviewSection from './FeasibilityReviewSection';
import SubmissionDetailsForm from './SubmissionDetailsForm';
import RetrospectiveForm from './RetrospectiveForm';
import ComplianceMatrixForm from './ComplianceMatrixForm';
import PhaseDDraftUploads from './PhaseDDraftUploads';
import AIScoreCard from './AIScoreCard';
import CeoSignOff from './CeoSignOff';
import ResumeScoringSection from './ResumeScoringSection';

const { Text, Title } = Typography;

interface WorkflowTrackerProps {
  workflowStatus: WorkflowStatusResponse;
  reviewGates: ReviewGateDto[];
  opportunityId: string;
  onAdvance: (reason: string) => Promise<void>;
  onRefresh: () => void;
}

const getStepStatus = (
  phaseStatus: string,
  overallStatus: string,
): 'finish' | 'process' | 'wait' | 'error' => {
  if (overallStatus === 'ON_HOLD' || overallStatus === 'CANCELLED') {
    if (phaseStatus === 'current') return 'error';
  }
  switch (phaseStatus) {
    case 'completed':
      return 'finish';
    case 'current':
      return 'process';
    case 'upcoming':
    default:
      return 'wait';
  }
};

const getStepIcon = (phaseStatus: string, overallStatus: string) => {
  if (overallStatus === 'ON_HOLD' && phaseStatus === 'current') {
    return <PauseCircleOutlined />;
  }
  if (overallStatus === 'CANCELLED' && phaseStatus === 'current') {
    return <CloseCircleOutlined />;
  }
  switch (phaseStatus) {
    case 'completed':
      return <CheckCircleOutlined />;
    case 'current':
      return <LoadingOutlined />;
    case 'upcoming':
    default:
      return <ClockCircleOutlined />;
  }
};

const overallStatusColors: Record<string, string> = {
  ACTIVE: 'processing',
  ON_HOLD: 'warning',
  WON: 'success',
  LOST: 'error',
  NO_BID: 'default',
  CANCELLED: 'default',
};

const overallStatusLabels: Record<string, string> = {
  ACTIVE: 'Active',
  ON_HOLD: 'On Hold',
  WON: 'Won',
  LOST: 'Lost',
  NO_BID: 'No Bid',
  CANCELLED: 'Cancelled',
};

const WorkflowTracker: React.FC<WorkflowTrackerProps> = ({
  workflowStatus,
  reviewGates,
  opportunityId,
  onAdvance,
  onRefresh,
}) => {
  const [advanceModalVisible, setAdvanceModalVisible] = useState(false);
  const [reason, setReason] = useState('');
  const [advancing, setAdvancing] = useState(false);

  // Change Owner state
  const [ownerModalVisible, setOwnerModalVisible] = useState(false);
  const [ownerModalPhase, setOwnerModalPhase] = useState<PhaseInfo | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined);
  const [selectedBackupUserId, setSelectedBackupUserId] = useState<string | undefined>(undefined);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submittingOwner, setSubmittingOwner] = useState(false);
  const [showAllUsers, setShowAllUsers] = useState(false);

  // Move to Phase state (ADMIN/CEO only)
  const [moveModalVisible, setMoveModalVisible] = useState(false);
  const [moveTargetPhase, setMoveTargetPhase] = useState<RfoPhase | undefined>(undefined);
  const [moveReason, setMoveReason] = useState('');
  const [moveAssignUserId, setMoveAssignUserId] = useState<string | undefined>(undefined);
  const [movingPhase, setMovingPhase] = useState(false);
  const [moveShowAllUsers, setMoveShowAllUsers] = useState(false);
  const [assignedCandidates, setAssignedCandidates] = useState<AssignedCandidate[]>([]);

  useEffect(() => {
    opportunityApi.getAssignedCandidates(opportunityId).then(setAssignedCandidates).catch(() => {});
  }, [opportunityId]);

  const currentPhaseInfo = workflowStatus.phases.find((p) => p.status === 'current');
  const currentIndex = workflowStatus.phases.findIndex((p) => p.status === 'current');

  // Track which phase the user is viewing (defaults to current phase)
  const [viewingIndex, setViewingIndex] = useState<number>(currentIndex >= 0 ? currentIndex : 0);
  useEffect(() => {
    setViewingIndex(currentIndex >= 0 ? currentIndex : 0);
  }, [currentIndex]);

  const viewingPhaseInfo = workflowStatus.phases[viewingIndex];
  const isViewingCurrent = viewingIndex === currentIndex;
  const isViewingCompleted = viewingPhaseInfo?.status === 'completed';

  // Fetch active users when owner modal opens
  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const response = await userApi.getAll({ size: 100, isActive: true });
      setUsers(response.content || []);
    } catch {
      message.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    if (ownerModalVisible || moveModalVisible) {
      fetchUsers();
    }
  }, [ownerModalVisible, moveModalVisible, fetchUsers]);

  const handleAdvance = async () => {
    setAdvancing(true);
    try {
      await onAdvance(reason);
      setAdvanceModalVisible(false);
      setReason('');
    } catch (err: any) {
      Modal.error({
        title: 'Cannot Advance',
        content: err.response?.data?.message || err.message || 'Failed to advance workflow',
      });
    } finally {
      setAdvancing(false);
    }
  };

  const handleChangeOwnerClick = (phaseInfo: PhaseInfo) => {
    setOwnerModalPhase(phaseInfo);
    setShowAllUsers(false);
    // Pre-select current owner if set
    setSelectedUserId(phaseInfo.ownerId || (phaseInfo.defaultOwnerUsers?.length === 1 ? phaseInfo.defaultOwnerUsers[0].id : undefined));
    setSelectedBackupUserId(phaseInfo.backupOwnerId || undefined);
    setOwnerModalVisible(true);
  };

  const handleOwnershipChange = async () => {
    if (!ownerModalPhase || !selectedUserId) {
      message.warning('Please select a new owner');
      return;
    }

    setSubmittingOwner(true);
    try {
      await workflowApi.changePhaseOwner(opportunityId, ownerModalPhase.phase, selectedUserId, selectedBackupUserId);
      message.success('Phase ownership changed successfully');
      setOwnerModalVisible(false);
      setOwnerModalPhase(null);
      setSelectedUserId(undefined);
      setSelectedBackupUserId(undefined);
      onRefresh();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      const errorMsg = err?.response?.data?.message || 'Failed to change phase ownership';
      message.error(errorMsg);
    } finally {
      setSubmittingOwner(false);
    }
  };

  const handleMoveToPhase = async () => {
    if (!moveTargetPhase || !moveReason.trim()) {
      message.warning('Please select a target phase and provide a reason');
      return;
    }
    setMovingPhase(true);
    try {
      await workflowApi.moveToPhase(opportunityId, moveTargetPhase, moveReason, moveAssignUserId);
      message.success(`Workflow moved to ${moveTargetPhase.replace('PHASE_', 'Phase ')}`);
      setMoveModalVisible(false);
      setMoveTargetPhase(undefined);
      setMoveReason('');
      setMoveAssignUserId(undefined);
      onRefresh();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      const errorMsg = err?.response?.data?.message || 'Failed to move workflow';
      message.error(errorMsg);
    } finally {
      setMovingPhase(false);
    }
  };

  // Handler for when a gate owner edits a completed phase — notify downstream owners + refresh
  const handleCompletedPhaseEdit = async () => {
    if (viewingPhaseInfo) {
      try {
        await workflowApi.notifyPhaseEdit(opportunityId, viewingPhaseInfo.phase);
      } catch {
        // Best-effort — don't block the user
        console.warn('Failed to send phase-edit notifications');
      }
    }
    onRefresh();
  };

  // Phase options for move modal — only previous (completed) phases
  const movePhaseOptions = workflowStatus.phases
    .filter((p) => p.status === 'completed')
    .map((p) => ({
      value: p.phase,
      label: `${p.gateLabel}`,
    }));

  const stepItems = workflowStatus.phases.map((phaseInfo: PhaseInfo) => {
    const gateParts = phaseInfo.gateLabel.split(': ');
    const gateShort = gateParts[0]; // "Gate A"

    return {
      title: (
        <Tooltip title={phaseInfo.gateLabel}>
          <span style={{ fontSize: 12 }}>{gateShort}</span>
        </Tooltip>
      ),
      status: getStepStatus(phaseInfo.status, workflowStatus.overallStatus),
      icon: getStepIcon(phaseInfo.status, workflowStatus.overallStatus),
    };
  });

  // Find the next phase label for the advance button
  const nextPhaseIndex = currentIndex + 1;
  const nextPhaseInfo =
    nextPhaseIndex < workflowStatus.phases.length ? workflowStatus.phases[nextPhaseIndex] : null;

  return (
    <Card
      style={{ marginBottom: 24 }}
      title={
        <Space>
          <Title level={5} style={{ margin: 0 }}>
            Workflow Progress
          </Title>
          <Badge
            status={overallStatusColors[workflowStatus.overallStatus] as any}
            text={
              <Text type="secondary" style={{ fontSize: 13 }}>
                {overallStatusLabels[workflowStatus.overallStatus] || workflowStatus.overallStatus}
              </Text>
            }
          />
        </Space>
      }
    >
      {/* Main Steps Pipeline — click any completed/current step to view */}
      <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
        <div style={{ minWidth: 700 }}>
          <Steps
            current={currentIndex >= 0 ? currentIndex : 0}
            items={stepItems}
            size="small"
            style={{ cursor: 'pointer' }}
            onChange={(idx) => {
              const phase = workflowStatus.phases[idx];
              if (phase && phase.status !== 'upcoming') {
                setViewingIndex(idx);
              }
            }}
          />
          {/* Phase labels rendered outside Steps to avoid Ant Design clipping */}
          <div style={{ display: 'flex', marginTop: 4, marginBottom: 16 }}>
            {workflowStatus.phases.map((phaseInfo: PhaseInfo, idx: number) => (
              <div
                key={phaseInfo.phase}
                style={{
                  flex: 1,
                  textAlign: 'center',
                  fontSize: 11,
                  color: idx <= currentIndex ? '#595959' : '#bfbfbf',
                  lineHeight: 1.4,
                  paddingLeft: 4,
                  paddingRight: 4,
                  cursor: phaseInfo.status !== 'upcoming' ? 'pointer' : 'default',
                }}
                onClick={() => {
                  if (phaseInfo.status !== 'upcoming') setViewingIndex(idx);
                }}
              >
                {phaseInfo.displayName}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Phase Detail Card — shows selected phase (current or completed read-only) */}
      {viewingPhaseInfo && (
        <Card
          size="small"
          style={{
            borderLeft: `4px solid ${getPhaseColor(viewingPhaseInfo.phase as RfoPhase)}`,
            marginBottom: 16,
          }}
        >
          {/* Banner for completed phases — editable by gate owner */}
          {isViewingCompleted && (
            <div style={{
              background: 'rgba(34, 197, 94, 0.12)', border: '1px solid #b7eb8f', borderRadius: 6,
              padding: '8px 12px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <Space>
                <EyeOutlined style={{ color: '#52c41a' }} />
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Viewing completed phase — <Text strong>{viewingPhaseInfo.gateLabel}</Text> (editable by gate owner)
                </Text>
              </Space>
              <Button type="link" size="small" onClick={() => setViewingIndex(currentIndex)}>
                Back to current phase
              </Button>
            </div>
          )}

          <Descriptions
            column={{ xs: 1, sm: 2 }}
            size="small"
            title={
              <Space>
                <Text strong>{viewingPhaseInfo.gateLabel}</Text>
                {isViewingCompleted && <Tag color="success">Completed</Tag>}
                {!isViewingCompleted && viewingPhaseInfo.documentCount > 0 && (
                  <Tag icon={<FileTextOutlined />} color="blue">
                    {viewingPhaseInfo.documentCount} artifact{viewingPhaseInfo.documentCount !== 1 ? 's' : ''}
                  </Tag>
                )}
              </Space>
            }
          >
            <Descriptions.Item label="Owner" span={2}>
              <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 2 }}>Primary</Text>
                  {viewingPhaseInfo.defaultOwnerUsers?.length > 0 || viewingPhaseInfo.ownerId ? (
                    <Space size={4}>
                      <Text strong>{viewingPhaseInfo.ownerName}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        ({viewingPhaseInfo.owner.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())})
                      </Text>
                    </Space>
                  ) : (
                    <Space size={4}>
                      <Tag color="orange">No user assigned</Tag>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Expected: {viewingPhaseInfo.owner.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                      </Text>
                    </Space>
                  )}
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 2 }}>Backup</Text>
                  {viewingPhaseInfo.backupOwnerName ? (
                    <Space size={4}>
                      <Text strong>{viewingPhaseInfo.backupOwnerName}</Text>
                      {viewingPhaseInfo.backupOwnerRole && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          ({viewingPhaseInfo.backupOwnerRole.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())})
                        </Text>
                      )}
                    </Space>
                  ) : (
                    <Tag>Not assigned</Tag>
                  )}
                </div>
                {isViewingCurrent && (
                  <Tooltip title="Change phase owners">
                    <Button
                      type="link"
                      size="small"
                      icon={<SwapOutlined />}
                      onClick={() => handleChangeOwnerClick(viewingPhaseInfo)}
                      style={{ fontSize: 12, padding: '0 4px', height: 'auto', color: '#1677ff', alignSelf: 'center' }}
                    >
                      Change
                    </Button>
                  </Tooltip>
                )}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Description">
              {getPhaseDescription(viewingPhaseInfo.phase as RfoPhase)}
            </Descriptions.Item>
            <Descriptions.Item label="Deliverables" span={2}>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {getPhaseDeliverables(viewingPhaseInfo.phase as RfoPhase).map((d, i) => (
                  <li key={i}><Text style={{ fontSize: 13 }}>{d}</Text></li>
                ))}
              </ul>
            </Descriptions.Item>
          </Descriptions>

          {/* Gate Requirements Checklist — only for current phase */}
          {isViewingCurrent && currentPhaseInfo?.gateRequirements && currentPhaseInfo.gateRequirements.length > 0 && (
            <div style={{ marginTop: 16, marginBottom: 8 }}>
              <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>Gate Requirements:</Text>
              {currentPhaseInfo.gateRequirements.map((req) => (
                <div key={req.key} style={{ padding: '4px 0' }}>
                  <Space>
                    {req.met ? (
                      <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />
                    )}
                    <Text style={{ fontSize: 13 }}>{req.label}</Text>
                    {!req.met && <Tag color="orange" style={{ fontSize: 11 }}>Required</Tag>}
                  </Space>
                </div>
              ))}
            </div>
          )}

          {/* AI Readiness Score — shows for current phase */}
          {isViewingCurrent && currentPhaseInfo?.gateRequirements && currentPhaseInfo.gateRequirements.length > 0 && (() => {
            const reqs = currentPhaseInfo.gateRequirements;
            const metCount = reqs.filter((r) => r.met).length;
            const totalCount = reqs.length;
            const score = Math.round((metCount / totalCount) * 100);
            const passed = score >= 80;
            const ownerName = currentPhaseInfo.ownerName || currentPhaseInfo.owner?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) || 'Phase Owner';

            return (
              <Card
                size="small"
                style={{
                  marginTop: 12,
                  marginBottom: 12,
                  borderRadius: 8,
                  borderLeft: `4px solid ${passed ? '#52c41a' : '#ff4d4f'}`,
                  background: passed ? 'rgba(34, 197, 94, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <Progress
                    type="circle"
                    percent={score}
                    size={56}
                    strokeColor={passed ? '#52c41a' : score >= 50 ? '#faad14' : '#ff4d4f'}
                    format={(pct) => <span style={{ fontSize: 16, fontWeight: 700 }}>{pct}%</span>}
                  />
                  <div style={{ flex: 1 }}>
                    <Text strong style={{ fontSize: 14, display: 'block' }}>
                      Gate Readiness: {metCount}/{totalCount} requirements met
                    </Text>
                    {passed ? (
                      <Text type="success" style={{ fontSize: 12 }}>
                        Score meets the 80% threshold — ready to advance
                      </Text>
                    ) : (
                      <Alert
                        type="warning"
                        showIcon
                        style={{ marginTop: 6, padding: '4px 12px' }}
                        message={
                          <Text style={{ fontSize: 12 }}>
                            Below 80% — <Text strong>{ownerName}</Text> needs to complete {totalCount - metCount} remaining requirement{totalCount - metCount > 1 ? 's' : ''} before advancing
                          </Text>
                        }
                      />
                    )}
                  </div>
                </div>
              </Card>
            );
          })()}

          {/* AI Analysis Score — for Phase B (bid) and Phase D (proposal) */}
          {isViewingCurrent && currentPhaseInfo?.phase === 'PHASE_B' && (
            <AIScoreCard opportunityId={opportunityId} type="bid" />
          )}
          {isViewingCurrent && currentPhaseInfo?.phase === 'PHASE_D' && (
            <AIScoreCard opportunityId={opportunityId} type="proposal" />
          )}

          {/* Phase content — editable by gate owners even for completed phases */}
          <div>
            {/* Phase A: Intake Details + Regenerate Requirements */}
            {viewingPhaseInfo.phase === 'PHASE_A' && (
              <>
                <IntakeDetailsForm opportunityId={opportunityId} onComplete={isViewingCurrent ? onRefresh : handleCompletedPhaseEdit} />
                <div style={{ marginTop: 16 }}>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => {
                      Modal.confirm({
                        title: 'Regenerate AI Requirements?',
                        content: 'This will delete the existing AI-extracted requirement documents and re-extract from the RFO source documents. The new extraction will appear in the Content Library under Phase A.',
                        okText: 'Regenerate',
                        okType: 'primary',
                        onOk: async () => {
                          try {
                            await analysisApi.reExtractRequirements(opportunityId);
                            message.success('Re-extraction started. New requirements will appear in the Content Library shortly.');
                          } catch {
                            message.error('Failed to start re-extraction.');
                          }
                        },
                      });
                    }}
                  >
                    Regenerate AI Requirements
                  </Button>
                </div>
              </>
            )}

            {/* Phase B: Feasibility Reviews → Bid Decision */}
            {viewingPhaseInfo.phase === 'PHASE_B' && (
              <>
                <FeasibilityReviewSection opportunityId={opportunityId} onComplete={isViewingCurrent ? onRefresh : handleCompletedPhaseEdit} />
                <BidDecisionForm opportunityId={opportunityId} onComplete={isViewingCurrent ? onRefresh : handleCompletedPhaseEdit} />
              </>
            )}

            {/* Phase C: Kickoff Plan + Resource Assignment */}
            {viewingPhaseInfo.phase === 'PHASE_C' && (
              <>
                <KickoffPlanForm opportunityId={opportunityId} onComplete={isViewingCurrent ? onRefresh : handleCompletedPhaseEdit} />
                <Card size="small" style={{ marginTop: 12, borderLeft: '3px solid #2563EB' }}
                  title={<Space><Text strong style={{ fontSize: 13 }}>Resource Assignment</Text>
                    {assignedCandidates.length > 0 && <Tag color="blue">{assignedCandidates.length} assigned</Tag>}
                  </Space>}
                >
                  {assignedCandidates.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {assignedCandidates.map((c) => (
                        <div key={`${c.candidateId}-${c.assignmentRole}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                          <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#2563EB', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600 }}>
                            {c.candidateName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Text style={{ fontSize: 12 }}>{c.candidateName}</Text>
                            {c.assignmentRole && <Text type="secondary" style={{ fontSize: 11 }}> — {c.assignmentRole}</Text>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      No resources assigned yet. Go to the Talent Pool to assign candidates to this RFO using AI-extracted role requirements.
                    </Text>
                  )}
                  <div style={{ marginTop: 8 }}>
                    <Button size="small" type="link" style={{ padding: 0, fontSize: 12 }}
                      onClick={() => window.open('/talent-pool', '_blank')}>
                      Open Talent Pool →
                    </Button>
                  </div>
                </Card>
              </>
            )}

            {/* Phase D: Compliance Matrix + Draft Uploads + Resume Scoring */}
            {viewingPhaseInfo.phase === 'PHASE_D' && (
              <>
                <ComplianceMatrixForm opportunityId={opportunityId} onComplete={isViewingCurrent ? onRefresh : handleCompletedPhaseEdit} />
                <PhaseDDraftUploads opportunityId={opportunityId} onUploadComplete={isViewingCurrent ? onRefresh : handleCompletedPhaseEdit} />
                <ResumeScoringSection opportunityId={opportunityId} />
              </>
            )}

            {/* Phase F: CEO Executive Sign-off */}
            {viewingPhaseInfo.phase === 'PHASE_F' && (
              <CeoSignOff opportunityId={opportunityId} reviewGates={reviewGates} onComplete={isViewingCurrent ? onRefresh : handleCompletedPhaseEdit} />
            )}

            {/* Phase G: Submission Details */}
            {viewingPhaseInfo.phase === 'PHASE_G' && (
              <SubmissionDetailsForm opportunityId={opportunityId} onComplete={isViewingCurrent ? onRefresh : handleCompletedPhaseEdit} />
            )}

            {/* Phase H: Post-Submission Retrospective */}
            {viewingPhaseInfo.phase === 'PHASE_H' && (
              <RetrospectiveForm opportunityId={opportunityId} onComplete={isViewingCurrent ? onRefresh : handleCompletedPhaseEdit} />
            )}
          </div>

          {/* Gate Documents (Artifacts) — for current and completed phases */}
          {(isViewingCurrent || isViewingCompleted) && (
            <div style={{ marginTop: 16 }}>
              <GateDocuments
                opportunityId={opportunityId}
                phase={viewingPhaseInfo.phase}
                onUploadComplete={isViewingCurrent ? onRefresh : handleCompletedPhaseEdit}
              />
            </div>
          )}

          {/* Action Buttons — only for current phase */}
          {isViewingCurrent && workflowStatus.overallStatus === 'ACTIVE' && (
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <Space>
                {/* Move to Phase — ADMIN/CEO only */}
                {workflowStatus.currentUserCanMovePhase && (
                  <Button
                    icon={<RetweetOutlined />}
                    onClick={() => setMoveModalVisible(true)}
                  >
                    Move to Phase
                  </Button>
                )}

                {/* Advance Button */}
                {nextPhaseInfo && (
                  <Tooltip
                    title={
                      !workflowStatus.canAdvance
                        ? 'Complete all gate requirements before advancing'
                        : !workflowStatus.currentUserCanAdvance
                        ? `Only the phase owner (${currentPhaseInfo?.ownerName}) or CEO can advance this gate`
                        : `Advance to ${nextPhaseInfo.gateLabel}`
                    }
                  >
                    <Button
                      type="primary"
                      icon={<RightOutlined />}
                      onClick={() => setAdvanceModalVisible(true)}
                      disabled={!workflowStatus.currentUserCanAdvance}
                      style={
                        workflowStatus.currentUserCanAdvance
                          ? {
                              backgroundColor: getPhaseColor(nextPhaseInfo.phase as RfoPhase),
                              borderColor: getPhaseColor(nextPhaseInfo.phase as RfoPhase),
                            }
                          : {}
                      }
                    >
                      Advance to {nextPhaseInfo.gateLabel.split(': ')[0]}
                    </Button>
                  </Tooltip>
                )}
              </Space>
            </div>
          )}
        </Card>
      )}

      {/* Review Sub-Gates — shown in Phase E (even if not yet initialized) OR when gates exist */}
      {(workflowStatus.currentPhase === 'PHASE_E' || (reviewGates && reviewGates.length > 0)) && (
        <div style={{ marginTop: 16 }}>
          <ReviewSubGates
            reviewGates={reviewGates}
            opportunityId={opportunityId}
            onGatesUpdated={() => onRefresh()}
            onRollback={() => onRefresh()}
          />
        </div>
      )}

      {/* Terminal states */}
      {(workflowStatus.overallStatus === 'WON' || workflowStatus.overallStatus === 'LOST') && (
        <Card
          size="small"
          style={{
            borderLeft: `4px solid ${workflowStatus.overallStatus === 'WON' ? '#52c41a' : '#ff4d4f'}`,
            background: workflowStatus.overallStatus === 'WON' ? 'rgba(34, 197, 94, 0.12)' : 'rgba(239, 68, 68, 0.12)',
          }}
        >
          <Text strong style={{ fontSize: 16 }}>
            {workflowStatus.overallStatus === 'WON'
              ? 'Opportunity Won!'
              : 'Opportunity Lost'}
          </Text>
        </Card>
      )}

      {/* Advance Modal */}
      <Modal
        title={
          nextPhaseInfo
            ? `Advance to ${nextPhaseInfo.gateLabel}`
            : 'Advance Workflow'
        }
        open={advanceModalVisible}
        onOk={handleAdvance}
        onCancel={() => {
          setAdvanceModalVisible(false);
          setReason('');
        }}
        confirmLoading={advancing}
        okText="Advance"
      >
        <div style={{ marginBottom: 16 }}>
          <Text>
            You are about to advance from{' '}
            <Text strong>{currentPhaseInfo?.gateLabel}</Text> to{' '}
            <Text strong>{nextPhaseInfo?.gateLabel}</Text>.
          </Text>
        </div>
        <Input.TextArea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for advancing (optional)..."
          rows={3}
        />
      </Modal>

      {/* Move to Phase Modal (ADMIN/CEO) */}
      <Modal
        title={
          <Space>
            <RetweetOutlined />
            <span>Move to Phase</span>
          </Space>
        }
        open={moveModalVisible}
        onCancel={() => {
          setMoveModalVisible(false);
          setMoveTargetPhase(undefined);
          setMoveReason('');
          setMoveAssignUserId(undefined);
          setMoveShowAllUsers(false);
        }}
        onOk={handleMoveToPhase}
        okText="Move Workflow"
        confirmLoading={movingPhase}
        okButtonProps={{ disabled: !moveTargetPhase || !moveReason.trim(), danger: true }}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            Current Phase: <Text strong>{currentPhaseInfo?.gateLabel}</Text>
          </Text>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Text style={{ display: 'block', marginBottom: 8 }}>Target Phase: <Text type="danger">*</Text></Text>
          <Select
            style={{ width: '100%' }}
            placeholder="Select target phase..."
            value={moveTargetPhase}
            onChange={(value) => setMoveTargetPhase(value)}
            options={movePhaseOptions}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <Text style={{ display: 'block', marginBottom: 8 }}>Assign to (optional):</Text>
          <Select
            style={{ width: '100%' }}
            placeholder="Search and select a user..."
            showSearch
            allowClear
            optionFilterProp="label"
            loading={loadingUsers}
            value={moveAssignUserId}
            onChange={(value) => setMoveAssignUserId(value)}
            options={(moveShowAllUsers
              ? users
              : moveTargetPhase
                ? users.filter((u) => {
                    const targetPhaseInfo = workflowStatus.phases.find((p) => p.phase === moveTargetPhase);
                    return targetPhaseInfo ? u.role === targetPhaseInfo.owner : true;
                  })
                : users
            ).map((u) => ({
              value: u.id,
              label: `${u.fullName} (${u.role.replace(/_/g, ' ')})`,
            }))}
          />
          <div style={{ marginTop: 6 }}>
            <Button
              type="link"
              size="small"
              style={{ fontSize: 12, padding: 0 }}
              onClick={() => setMoveShowAllUsers(!moveShowAllUsers)}
            >
              {moveShowAllUsers ? 'Filter by target phase role' : 'Show all users'}
            </Button>
          </div>
        </div>

        <div>
          <Text style={{ display: 'block', marginBottom: 8 }}>Reason: <Text type="danger">*</Text></Text>
          <Input.TextArea
            value={moveReason}
            onChange={(e) => setMoveReason(e.target.value)}
            placeholder="Provide a reason for moving this workflow..."
            rows={3}
          />
        </div>
      </Modal>

      {/* Change Phase Owner Modal */}
      <Modal
        title={
          <Space>
            <SwapOutlined />
            <span>Change Phase Owners</span>
          </Space>
        }
        open={ownerModalVisible}
        onCancel={() => {
          setOwnerModalVisible(false);
          setOwnerModalPhase(null);
          setSelectedUserId(undefined);
          setSelectedBackupUserId(undefined);
        }}
        onOk={handleOwnershipChange}
        okText="Save Ownership"
        confirmLoading={submittingOwner}
        okButtonProps={{ disabled: !selectedUserId }}
        destroyOnClose
      >
        {ownerModalPhase && (
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              Phase: <Text strong>{ownerModalPhase.gateLabel}</Text>
            </Text>
            <br />
            <Text type="secondary">
              Role: <Text strong>{ownerModalPhase.owner.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</Text>
            </Text>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <Text style={{ display: 'block', marginBottom: 8 }}>Primary Owner: <Text type="danger">*</Text></Text>
          <Select
            style={{ width: '100%' }}
            placeholder="Search and select primary owner..."
            showSearch
            optionFilterProp="label"
            loading={loadingUsers}
            value={selectedUserId}
            onChange={(value) => setSelectedUserId(value)}
            options={(showAllUsers
              ? users
              : users.filter((u) => u.role === ownerModalPhase?.owner)
            ).filter((u) => u.id !== selectedBackupUserId).map((u) => ({
              value: u.id,
              label: `${u.fullName} (${u.role.replace(/_/g, ' ')})`,
            }))}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <Text style={{ display: 'block', marginBottom: 8 }}>Backup Owner:</Text>
          <Select
            style={{ width: '100%' }}
            placeholder="Search and select backup owner..."
            showSearch
            allowClear
            optionFilterProp="label"
            loading={loadingUsers}
            value={selectedBackupUserId}
            onChange={(value) => setSelectedBackupUserId(value)}
            options={users
              .filter((u) => u.id !== selectedUserId)
              .map((u) => ({
                value: u.id,
                label: `${u.fullName} (${u.role.replace(/_/g, ' ')})`,
              }))}
          />
        </div>

        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {showAllUsers
              ? 'Showing all active users'
              : `Primary filtered by ${ownerModalPhase?.owner?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) ?? 'role'}`}
          </Text>
          {' — '}
          <Button
            type="link"
            size="small"
            style={{ fontSize: 12, padding: 0 }}
            onClick={() => setShowAllUsers(!showAllUsers)}
          >
            {showAllUsers ? 'Filter by role' : 'Show all users'}
          </Button>
        </div>
      </Modal>
    </Card>
  );
};

export default WorkflowTracker;
